import React, { useEffect, useState } from "react";

import {
  Button,
  DocumentList,
  InfoPanel,
  Select,
  SelectData,
  SelectEvent,
  useStepperDispatch,
  TextField,
  Typography,
  Modal,
  Checkbox,
} from "@king-ict/eupisi-ui/components";
import { ReactComponent as UploadIcon } from "@king-ict/eupisi-ui/components/Icon/Upload.svg";
import { useValidation } from "@king-ict/eupisi-ui/hooks";
import { hrDate, nameof } from "@king-ict/eupisi-ui/utils";
import { Col, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useRouteMatch } from "react-router";

import { useTypedSelector } from "../../../app/rootReducer";
import { useGetNationalities } from "../../../hooks/codebook/useGetNationalities";
import { useGetNationalMinorities } from "../../../hooks/codebook/useGetNationalMinorities";
import { useGetParentNumbers } from "../../../hooks/codebook/useGetParentNumbers";
import { useGetParentshipTypes } from "../../../hooks/codebook/useGetParentshipTypes";
import { useGetProfessionalQualifications } from "../../../hooks/codebook/useGetProfessionalQualifications";
import useInput from "../../../hooks/input/useInput";
import ApplicantParent from "../../../models/Person/ApplicantParent";
import ApplicantParentValidationSchema from "../../../models/Person/ApplicantParentValidationSchema";
import IntegrationResultStatusEnum from "../../../models/Person/IntegrationResultStatusEnum";
import ParentNumberEnum from "../../../models/Person/ParentNumberEnum";
import ParentshipTypeEnum from "../../../models/Person/ParentshipTypeEnum";
import RequestTypeEnum from "../../../models/Request/RequestTypeEnum";
import RequestValidationSchema from "../../../models/Request/RequestValidationSchema";
import CodeBook from "../../../models/Shared/CodeBook";
import {
  newPreschoolRequestRoute,
  draftPreschoolRequestRoute,
  editPreschoolRequestRoute,
} from "../../../routes/routes";
import {
  selectAllDocuments,
  updateRequestApplicant,
  updateRequestParentNumber,
  updateRequestType,
} from "../../../slices/requestSlice";
import DocumentsModal from "../DocumentsModal/DocumentsModal";
import { useOtherParentDocuments } from "../OtherParentDataStep/useOtherParentDocuments";
import styles from "./ApplicantParentDataStep.module.scss";
import { useApplicantParentDocuments } from "./useApplicantParentDocuments";

export default function ApplicantParentDataStep() {
  const checkIsCustodyConfirmed = () => {
    let search = window.location.search;
    let params = new URLSearchParams(search);
    const paramIsParent = params?.get("IsCustodyConfirmed");
    if (paramIsParent === "undefined" || paramIsParent === null) {
      return true;
    } else {
      return paramIsParent === "true";
    }
  };

  const [isCustodyConfirmed] = useState(() => checkIsCustodyConfirmed());

  const [applicant, setApplicant] = useState<ApplicantParent>(
    {} as ApplicantParent,
  );

  const applicantParentState = useTypedSelector(
    (state) => state.request.request.applicantParent,
  );
  const requestState = useTypedSelector((state) => state.request.request);

  const allDocuments = useSelector(selectAllDocuments);

  const [isConfirmModalOpened, setIsConfirmModalOpened] = useState(false);
  const [isMessageModalOpened, setIsMessageModalOpened] = useState(false);

  const {
    isDocumentsModalOpen,
    handleUploadDocuments,
    handleUploadExistingDocument,
    handleConfirmDocumentsModal,
    handleCloseDocumentsModal,
    handleToggleDocumentsModal,
    handleDownloadDocument,
    handleRemoveDocumentFromList,
    handleRemoveDocument,
  } = useApplicantParentDocuments({
    requestState: requestState,
    setApplicant: setApplicant,
  });

  const {
    handleRemoveDocuments: handleRemoveOtherParentDocuments,
  } = useOtherParentDocuments();

  useStepperDispatch({
    type: "next",
    payload: () => {
      return handleValidationForNextStep();
    },
  });

  const nationalMinorities = useGetNationalMinorities();
  const nationalities = useGetNationalities();
  const professionalQualifications = useGetProfessionalQualifications();
  const parentshipTypes = useGetParentshipTypes();
  const parentNumbers = useGetParentNumbers();

  const newPreschoolRequestMatch = useRouteMatch(newPreschoolRequestRoute());
  const draftPreschoolRequestMatch = useRouteMatch(
    draftPreschoolRequestRoute(),
  );
  const editPreschoolRequestMatch = useRouteMatch(editPreschoolRequestRoute());

  useEffect(() => {
    if (
      !Object.keys(applicant).length &&
      applicantParentState &&
      !!Object.keys(applicantParentState).length
    ) {
      setApplicant(applicantParentState);
    }
  }, [applicant, applicantParentState]);

  useEffect(() => {
    if (!Object.keys(applicant).length || applicant.nationalityId != null) {
      return;
    }

    const defaultNationality = nationalities?.find(
      (nationality) => nationality.id === 999,
    );

    if (!defaultNationality) {
      return;
    }

    setApplicant((prevApplicant) => ({
      ...prevApplicant,
      nationalityId: defaultNationality.id,
      nationalityName: defaultNationality.name,
    }));
  }, [applicant, nationalities]);

  useEffect(() => {
    if (!Object.keys(applicant).length) {
      return;
    }

    const nextIsCustodyConfirmed =
      applicant.isCustodyConfirmed ?? isCustodyConfirmed;
    const nextIsConfirmedByDocument =
      applicant.isConfirmedParentChildRelationByDocument ??
      nextIsCustodyConfirmed;

    if (
      applicant.isCustodyConfirmed === nextIsCustodyConfirmed &&
      applicant.isConfirmedParentChildRelationByDocument ===
        nextIsConfirmedByDocument
    ) {
      return;
    }

    setApplicant((prevApplicant) => ({
      ...prevApplicant,
      isCustodyConfirmed: nextIsCustodyConfirmed,
      isConfirmedParentChildRelationByDocument: nextIsConfirmedByDocument,
    }));
  }, [applicant, isCustodyConfirmed]);

  useEffect(() => {
    if (
      !!Object.keys(applicant).length &&
      !applicant.isCustodyConfirmed &&
      (!applicant?.documents || applicant?.documents?.length < 1) &&
      applicant.isConfirmedParentChildRelationByDocument
    ) {
      setApplicant((prevApplicant) => ({
        ...prevApplicant,
        isConfirmedParentChildRelationByDocument: false,
      }));
    }
  }, [applicant]);

  const { t } = useTranslation();
  const dispatch = useDispatch();

  const { handleChange, handleMultipleChange } = useInput();

  const [applicantErrors, doApplicantValidations] = useValidation({
    data: applicant,
    validationSchema: ApplicantParentValidationSchema,
  });

  const [requestErrors, doRequestValidations] = useValidation({
    data: requestState,
    validationSchema: RequestValidationSchema,
  });

  const handleParentNumberChange = (e: React.ChangeEvent<SelectEvent>) => {
    if (
      e.target.value === ParentNumberEnum.OneParent &&
      requestState &&
      requestState.otherParent &&
      requestState.otherParent.documents
    ) {
      handleRemoveOtherParentDocuments(requestState.otherParent.documents);
    }
    dispatch(updateRequestParentNumber(e.target.value as ParentNumberEnum));
  };

  const handleParentshipTypeChange = (e: any) => {
    const { value } = e.target;
    handleMultipleChange(
      e,
      nameof<CodeBook>("id"),
      parentshipTypes,
      [
        {
          valueProp: nameof<CodeBook>("id"),
          stateProp: nameof<typeof applicant>("parentshipTypeId"),
        },
        {
          valueProp: nameof<CodeBook>("name"),
          stateProp: nameof<typeof applicant>("parentshipTypeName"),
        },
      ],
      setApplicant,
    );

    if (+value !== ParentshipTypeEnum.Custodian) {
      setApplicant((prev) => ({ ...prev, custody: undefined }));
    }
  };

  const handleValidationForNextStep = () => {
    if (applicant) {
      const isValid = doApplicantValidations() && doRequestValidations();
      if (isValid) {
        if (applicant.isCustodyConfirmed) {
          handleNextStep();
        } else {
          if (
            applicant?.documents === null ||
            applicant?.documents?.length < 1 ||
            !applicant.isConfirmedParentChildRelationByDocument
          ) {
            setIsMessageModalOpened((prev) => !prev);
            return false;
          } else {
            handleNextStep();
          }
        }
      } else {
        return isValid;
      }
    } else {
      return false;
    }
  };

  const handleNextStep = () => {
    setApplicant(applicant);
    dispatch(updateRequestApplicant(applicant));
    dispatch(
      updateRequestType(
        newPreschoolRequestMatch != null ||
          editPreschoolRequestMatch != null ||
          draftPreschoolRequestMatch != null
          ? RequestTypeEnum.Preschool
          : RequestTypeEnum.Kindergarten,
      ),
    );
  };

  const handleToggleConfirmModal = () => {
    setIsConfirmModalOpened((prev) => !prev);
  };

  const handleToggleMessageModal = () => {
    setIsMessageModalOpened((prev) => !prev);
  };

  const handleConfirmActionModal = () => {
    handleNextStep();
  };

  const emptyValue = "-";

  return (
    <>
      <Row>
        <Col md={12}>
          <InfoPanel
            message={t("person.mainInfoMessage")}
            color="success"
            className={styles.mainInfoMessage}
            fullWidth
          />
        </Col>
      </Row>
      <Row className={styles.rowHeadingSpace}>
        <Col md={12}>
          <Typography component="h2" variant="largeBold">
            {applicant?.firstName && applicant.lastName
              ? `${applicant.firstName} ${applicant.lastName}`
              : t("applicantParentDataStep.header")}
          </Typography>
        </Col>
      </Row>
      <Row className={styles.rowHeadingSpace}>
        <Col md={12}>
          <Typography component="h2" variant="smallHeading">
            {t("request.basicInformation")}
          </Typography>
        </Col>
        <Col md={12}>
          {(applicant.integrationResultStatus &
            IntegrationResultStatusEnum.OibFailed) ===
            IntegrationResultStatusEnum.OibFailed && (
            <InfoPanel
              message={t("request.basicInformationDataErrorMessage")}
              color="danger"
              className={styles.errorMessage}
            />
          )}
        </Col>
      </Row>
      <Row className={styles.rowSpace}>
        <Col md={4}>
          <TextField
            type="text"
            label={t("person.firstName")}
            helperText={t("common.birthRegistry")}
            value={applicant?.firstName || emptyValue}
            plaintext
            bold
          />
        </Col>
        <Col md={4}>
          <TextField
            type="text"
            label={t("person.lastName")}
            helperText={t("common.birthRegistry")}
            value={applicant?.lastName || emptyValue}
            plaintext
            bold
          />
        </Col>
        <Col md={4}>
          <TextField
            type="text"
            label={t("person.oib")}
            helperText={t("common.oibApi")}
            value={applicant?.oib || emptyValue}
            plaintext
            bold
          />
        </Col>
      </Row>
      <Row className={styles.rowSpace}>
        <Col md={4}>
          <TextField
            type="text"
            label={t("person.dateOfBirth")}
            helperText={t("common.birthRegistry")}
            value={
              applicant?.dateOfBirth
                ? hrDate(applicant?.dateOfBirth)
                : emptyValue
            }
            plaintext
            bold
          />
        </Col>
        <Col md={4}>
          <TextField
            type="text"
            label={t("person.sex")}
            helperText={t("common.birthRegistry")}
            value={applicant?.sexName || emptyValue}
            plaintext
            bold
          />
        </Col>
        <Col md={4}>
          <TextField
            type="text"
            label={t("person.birthplace")}
            helperText={t("common.birthRegistry")}
            value={applicant?.birthplaceName || emptyValue}
            plaintext
            bold
          />
        </Col>
      </Row>
      <Row className={styles.rowSpace}>
        <Col md={4}>
          <TextField
            type="text"
            label={t("person.countryBirth")}
            helperText={t("common.birthRegistry")}
            value={applicant?.countryOfBirthName || emptyValue}
            plaintext
            bold
          />
        </Col>
        <Col md={4}>
          <TextField
            type="text"
            label={t("person.citizenship")}
            helperText={t("common.birthRegistry")}
            value={applicant?.citizenshipName || emptyValue}
            plaintext
            bold
          />
        </Col>
      </Row>
      <Row className={styles.rowSpace}>
        <Col md={4}>
          <Select
            emptyOption={{ value: undefined, text: "" }}
            label={t("person.nationalMinority")}
            name={nameof<typeof applicant>("nationalMinorityId")}
            value={applicant?.nationalMinorityId || undefined}
            onChange={(e) =>
              handleMultipleChange(
                e,
                nameof<CodeBook>("id"),
                nationalMinorities,
                [
                  {
                    valueProp: nameof<CodeBook>("id"),
                    stateProp: nameof<typeof applicant>("nationalMinorityId"),
                  },
                  {
                    valueProp: nameof<CodeBook>("name"),
                    stateProp: nameof<typeof applicant>("nationalMinorityName"),
                  },
                ],
                setApplicant,
              )
            }
            selectData={
              {
                valueProp: "id",
                textProp: "name",
                data: nationalMinorities || [],
              } as SelectData<CodeBook>
            }
          />
        </Col>
        <Col md={4}>
          <Select
            label={t("person.nationality")}
            name={nameof<typeof applicant>("nationalityId")}
            value={applicant?.nationalityId || undefined}
            onChange={(e) =>
              handleMultipleChange(
                e,
                nameof<CodeBook>("id"),
                nationalities,
                [
                  {
                    valueProp: nameof<CodeBook>("id"),
                    stateProp: nameof<typeof applicant>("nationalityId"),
                  },
                  {
                    valueProp: nameof<CodeBook>("name"),
                    stateProp: nameof<typeof applicant>("nationalityName"),
                  },
                ],
                setApplicant,
              )
            }
            selectData={
              {
                valueProp: "id",
                textProp: "name",
                data: nationalities || [],
              } as SelectData<CodeBook>
            }
            errorText={applicantErrors?.nationalityId}
          />
        </Col>
        <Col md={4}>
          <Select
            required
            label={t("person.parentNumber")}
            name={nameof<typeof requestState>("parentNumberId")}
            value={requestState?.parentNumberId || undefined}
            onChange={handleParentNumberChange}
            selectData={
              {
                valueProp: "id",
                textProp: "name",
                data: parentNumbers || [],
              } as SelectData<CodeBook>
            }
            errorText={requestErrors?.parentNumberId}
          />
        </Col>
      </Row>
      <Row className={styles.rowSpace}>
        <Col md={4}>
          <TextField
            required
            type="text"
            label={t("person.profession")}
            name={nameof<typeof applicant>("professionName")}
            value={applicant?.professionName || ""}
            onChange={(e) => handleChange(e, setApplicant)}
            errorText={applicantErrors?.professionName}
          />
        </Col>
        <Col md={4}>
          <Select
            required
            label={t("person.professionalQualification")}
            name={nameof<typeof applicant>("professionalQualificationId")}
            value={applicant?.professionalQualificationId || undefined}
            onChange={(e) =>
              handleMultipleChange(
                e,
                nameof<CodeBook>("id"),
                professionalQualifications,
                [
                  {
                    valueProp: nameof<CodeBook>("id"),
                    stateProp: nameof<typeof applicant>(
                      "professionalQualificationId",
                    ),
                  },
                  {
                    valueProp: nameof<CodeBook>("name"),
                    stateProp: nameof<typeof applicant>(
                      "professionalQualificationName",
                    ),
                  },
                ],
                setApplicant,
              )
            }
            selectData={
              {
                valueProp: "id",
                textProp: "name",
                data: professionalQualifications || [],
              } as SelectData<CodeBook>
            }
            errorText={applicantErrors?.professionalQualificationId}
          />
        </Col>
        <Col md={4}>
          <TextField
            required
            type="text"
            label={t("person.email")}
            name={nameof<typeof applicant>("email")}
            value={applicant?.email || ""}
            onChange={(e) => handleChange(e, setApplicant)}
            errorText={applicantErrors?.email}
          />
        </Col>
      </Row>
      <Row className={styles.rowSpace}>
        <Col md={4}>
          <TextField
            required
            type="text"
            label={t("person.phoneNumber")}
            name={nameof<typeof applicant>("phoneNumber")}
            value={applicant?.phoneNumber || ""}
            onChange={(e) => handleChange(e, setApplicant)}
            errorText={applicantErrors?.phoneNumber}
          />
        </Col>
        <Col md={4}>
          <Select
            required
            label={t("person.parentshipType")}
            name={nameof<typeof applicant>("parentshipTypeId")}
            value={applicant?.parentshipTypeId || undefined}
            onChange={handleParentshipTypeChange}
            selectData={
              {
                valueProp: "id",
                textProp: "name",
                data: parentshipTypes || [],
              } as SelectData<CodeBook>
            }
            errorText={applicantErrors?.parentshipTypeId}
          />
        </Col>
        <Col md={4}>
          {applicant?.parentshipTypeId === ParentshipTypeEnum.Custodian && (
            <TextField
              required
              type="text"
              label={`${t("person.custodian")}`}
              name={nameof<typeof applicant>("custody")}
              value={applicant?.custody || ""}
              onChange={(e) => handleChange(e, setApplicant)}
              errorText={applicantErrors?.custody}
            />
          )}
        </Col>
      </Row>
      <Row className={styles.rowHeadingSpace}>
        <Col md={12}>
          <Typography component="h3" variant="smallHeading">
            {t("person.permanentResidenceInformation")}
          </Typography>
        </Col>
      </Row>
      <Row className={styles.rowHeadingSpace}>
        <Col md={12}>
          {(applicant.integrationResultStatus &
            IntegrationResultStatusEnum.OibFailed) ===
          IntegrationResultStatusEnum.OibFailed ? (
            <InfoPanel
              message={t("person.parentPermanentResidenceError")}
              color="danger"
              className={styles.errorMessage}
            />
          ) : (
            <InfoPanel
              message={t("common.mupResidenceInfoMessage")}
              color="success"
            />
          )}
        </Col>
      </Row>

      <Row className={styles.rowSpace}>
        <Col md={4}>
          <TextField
            type="text"
            label={`${t("person.permanentResidence")} - ${t("person.country")}`}
            value={applicant?.permanentResidence?.countryName || emptyValue}
            plaintext
            bold
          />
        </Col>
        <Col md={4}>
          <TextField
            type="text"
            label={`${t("person.permanentResidence")} - ${t(
              "person.municipality",
            )}`}
            value={
              applicant?.permanentResidence?.municipalityName || emptyValue
            }
            plaintext
            bold
          />
        </Col>
        <Col md={4}>
          <TextField
            type="text"
            label={`${t("person.permanentResidence")} - ${t("person.town")}`}
            value={applicant?.permanentResidence?.townName || emptyValue}
            plaintext
            bold
          />
        </Col>
      </Row>
      <Row className={styles.rowSpace}>
        <Col md={4}>
          <TextField
            type="text"
            label={`${t("person.permanentResidence")} - ${t(
              "person.postalCode",
            )}`}
            value={applicant?.permanentResidence?.townPostalCode || emptyValue}
            plaintext
            bold
          />
        </Col>
        <Col md={4}>
          <TextField
            type="text"
            label={`${t("person.permanentResidence")} - ${t("person.street")}`}
            value={applicant?.permanentResidence?.street || emptyValue}
            plaintext
            bold
          />
        </Col>
        <Col md={4}>
          <TextField
            type="text"
            label={`${t("person.permanentResidence")} - ${t(
              "person.streetNumber",
            )}`}
            value={applicant?.permanentResidence?.streetNumber || emptyValue}
            plaintext
            bold
          />
        </Col>
      </Row>
      <Row className={styles.rowSpace}>
        <Col md={4}>
          <TextField
            type="text"
            label={`${t("person.permanentResidence")} - ${t(
              "person.streetNumberAddition",
            )}`}
            value={
              applicant?.permanentResidence?.streetNumberAddition || emptyValue
            }
            plaintext
            bold
          />
        </Col>
        <Col md={4}>
          <TextField
            type="text"
            label={`${t("person.permanentResidence")} - ${t(
              "person.dateFrom",
            )}`}
            value={
              applicant?.permanentResidence?.dateFrom
                ? hrDate(applicant.permanentResidence.dateFrom)
                : emptyValue
            }
            plaintext
            bold
          />
        </Col>
      </Row>
      {applicant?.temporaryResidence && (
        <>
          <Row className={styles.rowHeadingSpace}>
            <Col md={12}>
              <Typography component="h3" variant="smallHeading">
                {t("person.temporaryResidenceInformation")}
              </Typography>
            </Col>
          </Row>
          <Row className={styles.rowHeadingSpace}>
            <Col md={12}>
              {(applicant.integrationResultStatus &
                IntegrationResultStatusEnum.MupFailed) ===
              IntegrationResultStatusEnum.MupFailed ? (
                <InfoPanel
                  message={t("person.parentTemporaryResidenceError")}
                  color="danger"
                  className={styles.errorMessage}
                />
              ) : (
                <InfoPanel
                  message={t("common.mupResidenceInfoMessage")}
                  color="success"
                />
              )}
            </Col>
          </Row>
          <Row className={styles.rowSpace}>
            <Col md={4}>
              <TextField
                type="text"
                label={`${t("person.temporaryResidence")} - ${t(
                  "person.country",
                )}`}
                value={applicant?.temporaryResidence?.countryName || emptyValue}
                plaintext
                bold
              />
            </Col>
            <Col md={4}>
              <TextField
                type="text"
                label={`${t("person.temporaryResidence")} - ${t(
                  "person.municipality",
                )}`}
                value={
                  applicant?.temporaryResidence?.municipalityName || emptyValue
                }
                plaintext
                bold
              />
            </Col>
            <Col md={4}>
              <TextField
                type="text"
                label={`${t("person.temporaryResidence")} - ${t(
                  "person.town",
                )}`}
                value={applicant?.temporaryResidence?.townName || emptyValue}
                plaintext
                bold
              />
            </Col>
          </Row>
          <Row className={styles.rowSpace}>
            <Col md={4}>
              <TextField
                type="text"
                label={`${t("person.temporaryResidence")} - ${t(
                  "person.postalCode",
                )}`}
                value={
                  applicant?.temporaryResidence?.townPostalCode || emptyValue
                }
                plaintext
                bold
              />
            </Col>
            <Col md={4}>
              <TextField
                type="text"
                label={`${t("person.temporaryResidence")} - ${t(
                  "person.street",
                )}`}
                value={applicant?.temporaryResidence?.street || emptyValue}
                plaintext
                bold
              />
            </Col>
            <Col md={4}>
              <TextField
                type="text"
                label={`${t("person.temporaryResidence")} - ${t(
                  "person.streetNumber",
                )}`}
                value={
                  applicant?.temporaryResidence?.streetNumber || emptyValue
                }
                plaintext
                bold
              />
            </Col>
          </Row>
          <Row className={styles.rowSpace}>
            <Col md={4}>
              <TextField
                type="text"
                label={`${t("person.temporaryResidence")} - ${t(
                  "person.streetNumberAddition",
                )}`}
                value={
                  applicant?.temporaryResidence?.streetNumberAddition ||
                  emptyValue
                }
                plaintext
                bold
              />
            </Col>
            <Col md={4}>
              <TextField
                type="text"
                label={`${t("person.temporaryResidence")} - ${t(
                  "person.dateFrom",
                )}`}
                value={
                  applicant?.temporaryResidence?.dateFrom
                    ? hrDate(applicant.temporaryResidence.dateFrom)
                    : emptyValue
                }
                plaintext
                bold
              />
            </Col>
          </Row>
        </>
      )}
      <Row className={styles.rowHeadingSpace}>
        <Col md={12}>
          <InfoPanel
            message={
              applicant.isCustodyConfirmed
                ? t("applicantAndOtherParentSteps.parentChildRelationConfirmed")
                : t(
                    "applicantAndOtherParentSteps.parentChildRelationNotConfirmed",
                  )
            }
            color={applicant.isCustodyConfirmed ? "success" : "danger"}
          />
        </Col>
      </Row>
      <Row className={styles.rowHeadingSpace}>
        <Col md={12}>
          <Typography component="h3" variant="smallHeading">
            {t("documentUpload.additionalParentDocumentation")}
          </Typography>
        </Col>
      </Row>
      <Row className={styles.rowHeadingSpace}>
        <Col md={12}>
          <InfoPanel
            message={t("documentUpload.infoMessage")}
            color="success"
          />
        </Col>
      </Row>
      {!applicant.isCustodyConfirmed && (
        <Row className={styles.rowHeadingSpace}>
          <Col md={12}>
            <InfoPanel
              message={t("applicantAndOtherParentSteps.attachDocument")}
              color="warning"
            />
          </Col>
        </Row>
      )}

      <Row className={styles.rowSpace}>
        <Col md={12}>
          <Button
            color="primary"
            disabled={applicant?.documents?.length >= 10 || false}
            outline
            icon={<UploadIcon />}
            onClick={handleToggleDocumentsModal}
          >
            {t("documentUpload.additionalDocumentationAttach")}
          </Button>
          <DocumentsModal
            open={isDocumentsModalOpen}
            allDocuments={allDocuments}
            onClose={handleCloseDocumentsModal}
            onConfirm={handleConfirmDocumentsModal}
            onUpload={handleUploadDocuments}
            onRemove={handleRemoveDocument}
            onDownload={handleDownloadDocument}
            onExistingDocumentClick={handleUploadExistingDocument}
          />
        </Col>
      </Row>
      <Row className={styles.rowSpace}>
        <Col md={12}>
          <Col md={12}>
            <Typography variant="smallHeading">
              {t("documentUpload.additionalDocumentationList")}
            </Typography>
          </Col>
          <DocumentList
            documents={applicant?.documents || []}
            onRemoveClick={handleRemoveDocumentFromList}
            onDocumentClick={handleDownloadDocument}
            fullWidth
          />
        </Col>
      </Row>
      {!applicant.isCustodyConfirmed &&
        applicant?.documents != null &&
        applicant?.documents?.length > 0 && (
          <Row>
            <Col md={12}>
              <Checkbox
                label={t(
                  "applicantAndOtherParentSteps.confirmationParentChildRelationByDocument",
                )}
                name={nameof<typeof applicant>(
                  "isConfirmedParentChildRelationByDocument",
                )}
                checked={applicant?.isConfirmedParentChildRelationByDocument}
                value={applicant?.isConfirmedParentChildRelationByDocument}
                onChange={(e) => handleChange(e, setApplicant)}
              />
            </Col>
          </Row>
        )}
      <Modal
        type={"confirm"}
        title={t(
          "applicantAndOtherParentSteps.confirmationThatTheDocumentIsAttachedTitle",
        )}
        cancelText={t("common.no")}
        confirmText={t("common.yes")}
        open={isConfirmModalOpened}
        onClose={handleToggleConfirmModal}
        onConfirm={handleConfirmActionModal}
      >
        {t(
          "applicantAndOtherParentSteps.confirmationThatTheDocumentIsAttached",
        )}
      </Modal>

      <Modal
        type={"message"}
        title={t("applicantAndOtherParentSteps.documentIsNotAttachedTitle")}
        confirmText={t("common.close")}
        open={isMessageModalOpened}
        onConfirm={handleToggleMessageModal}
        onClose={handleToggleMessageModal}
      >
        {t("applicantAndOtherParentSteps.documentIsNotAttached")}
      </Modal>
    </>
  );
}
